import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  CreateCompanyPaymentSettingDto,
  UpdateCompanyPaymentSettingDto,
  InstallmentOptionDto,
} from "@/dto/InstallmentDto";
import {
  createInstallmentSetting,
  updateInstallmentSetting,
  getInstallmentSetting,
  getCompanyStamps,
  getSubscriptionProducts,
  CompanyStampDto,
  SubscriptionProductDto,
} from "@/apis/installment";
import { toast } from "react-hot-toast";

interface FormData {
  comId: number;
  productId: number;
  allowInstallment: string;
  maxInstallmentCount: number;
  minInstallmentAmount: number;
  availableInstallments: number[];
}

interface UseInstallmentSettingCreateReturn {
  control: any;
  handleSubmit: any;
  onSubmit: (data: FormData) => Promise<void>;
  isEdit: boolean;
  loading: boolean;
  companies: CompanyStampDto[];
  products: SubscriptionProductDto[];
  selectedCompany: CompanyStampDto | null;
  selectedProduct: SubscriptionProductDto | null;
  availableInstallmentOptions: InstallmentOptionDto[];
  handleGoBack: () => void;
  getAvailableInstallments: () => InstallmentOptionDto[];
  formatCurrency: (amount: number) => string;
  calculateInstallmentAmount: (
    totalAmount: number,
    installments: number
  ) => number;
  errors: any;
}

const useInstallmentSettingCreate = (): UseInstallmentSettingCreateReturn => {
  const navigate = useNavigate();
  const { settingId } = useParams<{ settingId: string }>();
  const isEdit = Boolean(settingId);

  // 폼 관리
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      comId: 0,
      productId: 0,
      allowInstallment: "Y",
      maxInstallmentCount: 4,
      minInstallmentAmount: 100,
      availableInstallments: [2, 3, 4],
    },
  });

  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<CompanyStampDto[]>([]);
  const [products, setProducts] = useState<SubscriptionProductDto[]>([]);

  // 폼 값 감시
  const watchedValues = watch();
  const selectedCompanyId = watchedValues.comId;
  const selectedProductId = watchedValues.productId;
  const maxInstallmentCount = watchedValues.maxInstallmentCount;
  const minInstallmentAmount = watchedValues.minInstallmentAmount;

  // 선택된 도장/상품 찾기
  const selectedCompany = useMemo(
    () => (companies || []).find((c) => c.comId === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const selectedProduct = useMemo(
    () =>
      (products || []).find((p) => p.productId === selectedProductId) || null,
    [products, selectedProductId]
  );

  // 사용 가능한 분할 옵션 계산
  const availableInstallmentOptions = useMemo(() => {
    if (!selectedProduct) return [];

    const options: InstallmentOptionDto[] = [];
    const totalAmount = selectedProduct.firstYearAmt;

    for (let i = 2; i <= (maxInstallmentCount || 12); i++) {
      const installmentAmount = Math.ceil(totalAmount / i);

      // 최소 분할 금액 체크
      if (minInstallmentAmount && installmentAmount < minInstallmentAmount) {
        continue;
      }

      options.push({
        installments: i,
        installmentAmount,
        totalAmount: installmentAmount * i,
      });
    }

    return options;
  }, [selectedProduct, maxInstallmentCount, minInstallmentAmount]);

  // 도장 목록 로드
  const loadCompanies = useCallback(async () => {
    try {
      console.log("🏢 도장 목록 로드 시작...");

      // 전체 도장 목록 조회 (페이징 없이 전체 조회)
      const response = await getCompanyStamps({
        page: 1,
        limit: 1000, // 충분히 큰 값으로 전체 조회
      });

      console.log("🏢 도장 목록 API 응답:", response);

      // 응답이 정상적이고 list가 존재하는 경우에만 설정
      if (response && response.list && Array.isArray(response.list)) {
        console.log(`✅ 도장 목록 ${response.list.length}개 로드 성공`);
        setCompanies(response.list);
      } else if (response && Array.isArray(response)) {
        // 응답이 직접 배열인 경우
        console.log(`✅ 도장 목록 ${response.length}개 로드 성공 (직접 배열)`);
        setCompanies(response);
      } else {
        console.warn("⚠️ 도장 목록 응답 형식이 예상과 다릅니다:", response);
        setCompanies([]);
      }
    } catch (error) {
      console.error("❌ 도장 목록 로드 실패:", error);

      // 에러 상세 정보 로그
      if (error.response) {
        console.error("응답 상태:", error.response.status);
        console.error("응답 데이터:", error.response.data);
      } else if (error.request) {
        console.error("요청 실패:", error.request);
      } else {
        console.error("에러 메시지:", error.message);
      }

      toast.error(
        `도장 목록을 불러오는데 실패했습니다: ${
          error.message || "알 수 없는 오류"
        }`
      );
      // 실패 시 빈 배열로 설정
      setCompanies([]);
    }
  }, []);

  // 상품 목록 로드
  const loadProducts = useCallback(async () => {
    try {
      console.log("📦 상품 목록 로드 시작...");

      const response = await getSubscriptionProducts();

      console.log("📦 상품 목록 API 응답:", response);

      if (
        response &&
        response.success &&
        response.data &&
        Array.isArray(response.data)
      ) {
        // 활성화된 상품만 필터링
        const activeProducts = response.data.filter(
          (product) => product.activeYn === "Y"
        );
        console.log(
          `✅ 상품 목록 ${activeProducts.length}개 로드 성공 (전체: ${response.data.length}개)`
        );
        setProducts(activeProducts);
      } else if (response && Array.isArray(response)) {
        // 응답이 직접 배열인 경우
        const activeProducts = response.filter(
          (product) => product.activeYn === "Y"
        );
        console.log(
          `✅ 상품 목록 ${activeProducts.length}개 로드 성공 (직접 배열, 전체: ${response.length}개)`
        );
        setProducts(activeProducts);
      } else {
        console.warn("⚠️ 상품 목록 응답이 예상과 다릅니다:", response);
        setProducts([]);
      }
    } catch (error) {
      console.error("❌ 상품 목록 로드 실패:", error);

      // 에러 상세 정보 로그
      if (error.response) {
        console.error("응답 상태:", error.response.status);
        console.error("응답 데이터:", error.response.data);
      } else if (error.request) {
        console.error("요청 실패:", error.request);
      } else {
        console.error("에러 메시지:", error.message);
      }

      toast.error(
        `상품 목록을 불러오는데 실패했습니다: ${
          error.message || "알 수 없는 오류"
        }`
      );
      // 실패 시 빈 배열로 설정
      setProducts([]);
    }
  }, []);

  // 마스터 데이터 로드
  const loadMasterData = useCallback(async () => {
    try {
      setLoading(true);
      // 동시에 도장과 상품 목록 로드
      await Promise.all([loadCompanies(), loadProducts()]);
    } catch (error) {
      console.error("마스터 데이터 로드 실패:", error);
      toast.error("기초 데이터를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [loadCompanies, loadProducts]);

  // 기존 설정 로드 (수정 모드)
  const loadExistingSetting = useCallback(async () => {
    if (!isEdit || !settingId) return;

    try {
      setLoading(true);
      const setting = await getInstallmentSetting(Number(settingId));

      // 폼에 기존 값 설정
      reset({
        comId: setting.comId,
        productId: setting.productId,
        allowInstallment: setting.allowInstallment,
        maxInstallmentCount: setting.maxInstallmentCount,
        minInstallmentAmount: setting.minInstallmentAmount,
        availableInstallments: JSON.parse(setting.availableInstallments),
      });
    } catch (error) {
      console.error("기존 설정 로드 실패:", error);
      toast.error("기존 설정을 불러오는데 실패했습니다.");
      navigate("/installment-setting");
    } finally {
      setLoading(false);
    }
  }, [isEdit, settingId, reset, navigate]);

  // 초기 데이터 로드
  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    if (isEdit) {
      loadExistingSetting();
    }
  }, [isEdit, loadExistingSetting]);

  // 폼 제출
  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        setLoading(true);

        if (isEdit && settingId) {
          // 수정
          const updateData: UpdateCompanyPaymentSettingDto = {
            allowInstallment: data.allowInstallment,
            maxInstallmentCount: data.maxInstallmentCount,
            minInstallmentAmount: data.minInstallmentAmount,
            availableInstallments: data.availableInstallments,
          };

          await updateInstallmentSetting(Number(settingId), updateData);
          toast.success("분할결제 설정이 수정되었습니다.");
        } else {
          // 생성
          const createData: CreateCompanyPaymentSettingDto = {
            comId: data.comId,
            productId: data.productId,
            allowInstallment: data.allowInstallment,
            maxInstallmentCount: data.maxInstallmentCount,
            minInstallmentAmount: data.minInstallmentAmount,
            availableInstallments: data.availableInstallments,
          };

          await createInstallmentSetting(createData);
          toast.success("분할결제 설정이 생성되었습니다.");
        }

        navigate("/installment-setting");
      } catch (error) {
        console.error("분할결제 설정 저장 실패:", error);
        toast.error(
          isEdit
            ? "분할결제 설정 수정에 실패했습니다."
            : "분할결제 설정 생성에 실패했습니다."
        );
      } finally {
        setLoading(false);
      }
    },
    [isEdit, settingId, navigate]
  );

  // 목록으로 돌아가기
  const handleGoBack = useCallback(() => {
    navigate("/installment-setting");
  }, [navigate]);

  // 현재 설정으로 가능한 분할 옵션 가져오기
  const getAvailableInstallments = useCallback(() => {
    return availableInstallmentOptions.filter((option) =>
      watchedValues.availableInstallments?.includes(option.installments)
    );
  }, [availableInstallmentOptions, watchedValues.availableInstallments]);

  // 통화 포맷팅
  const formatCurrency = useCallback((amount: number) => {
    return `$${amount.toLocaleString()}`;
  }, []);

  // 분할 금액 계산
  const calculateInstallmentAmount = useCallback(
    (totalAmount: number, installments: number) => {
      return Math.ceil(totalAmount / installments);
    },
    []
  );

  return {
    control,
    handleSubmit,
    onSubmit,
    isEdit,
    loading,
    companies,
    products,
    selectedCompany,
    selectedProduct,
    availableInstallmentOptions,
    handleGoBack,
    getAvailableInstallments,
    formatCurrency,
    calculateInstallmentAmount,
    errors,
  };
};

export default useInstallmentSettingCreate;
